import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import useQueryFetch from "../../../modules/api/apiHooks/useQueryFetch";
import { getRtkHeader } from "../../../modules/api/fetch/getHeaders";
import { useAuth, useAuthMutate } from "../../../modules/auth/authHooks";
import { removeUserInfo } from "../../../modules/auth/userDataActions";
import { appVersion, staticImages, Variables } from "../../../modules/configs/appConfigs";
import { LOGOUT } from "../../../modules/graphql/authGraphQL";
import LocalStorageService from "../../../modules/helpers/localStorageService";
import { setFocus } from "../../../modules/spatialNavigation";
import { useIspSelector } from "../../../modules/isp/ispHooks";
import { getSubscriptionPlan } from "../../../modules/shared/subscriptionService";
import { FocusContext, useFocusable } from "../../../modules/spatialNavigation";
import { ToastActionType } from "../../../modules/toast/context/toast.interface";
import { useToast } from "../../../modules/toast/context/toastContextHooks";
import FocusableButton from "../../shared/focusableButton/FocusableButton";
import IconComponent from "../../shared/icon/IconComponent";
import MenuItem from "../menuItem/MenuItem";
import ProfileImage from "./ProfileImage";
import * as styles from "./styles.module.scss";
import ConfirmationDialog from "../../shared/dialogs/confirmationDialog/ConfirmationDialog";
import clsx from "../../../modules/helpers/clsx";

const Sidebar = () => {
  const auth = useAuth();
  const history = useHistory();
  const ispName = useIspSelector();
  const authDispatch = useAuthMutate();
  const [subscriptionStatus, setSubscriptionStatus] = useState<{ label: string; className: string }>(null);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [logout, logoutContext] = useQueryFetch(process.env.TV_AUTH_GRAPHQL_ENDPOINT!, LOGOUT, getRtkHeader, true);
  const toast = useToast();
  const { ref, focusKey } = useFocusable({
    focusable: true,
    saveLastFocusedChild: false,
    trackChildren: true,
    autoRestoreFocus: true,
    isFocusBoundary: true,
    focusBoundaryDirections: ["up"],
    preferredChildFocusKey: undefined,
    onEnterPress: () => {},
    onEnterRelease: () => {},
    onArrowPress: () => true,
    onFocus: () => {},
    onBlur: () => {},
  });

  const handleLogout = () => {
    toast({ message: " خروج با موفقیت انجام شد.", options: { type: ToastActionType.SUCCESS, duration: 2000 } });
    logout().then(() => {
      LocalStorageService.delete(Variables.accessToken);
      LocalStorageService.delete(Variables.refreshToken);
      authDispatch(removeUserInfo());
      history.push("/preparation/logout");
    });
  };

  useEffect(() => {
    if (auth.user) {
      setSubscriptionStatus(getSubscriptionPlan(auth.user, ispName));
    }
  }, [auth.user, ispName]);

  const handleOpenLogoutDialog = useCallback(() => {
    setShowLogoutDialog(true);
    setFocus("confirmDialogButton");
  }, []);

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles.sidebarContainer} ref={ref}>
        <nav className={`${styles.menuList} ${styles.menuListFocused}`}>
          <span className={styles.logoContainer}>
            <img className={styles.logoImage} src={staticImages.logo} />
          </span>
          {auth && auth.user && (
            <>
              <FocusableButton
                focusKey="profile"
                fullWidth
                className={styles.profileChangeButton}
                onClick={() => history.push("/profile")}
              >
                <ProfileImage />
              </FocusableButton>
              {!!subscriptionStatus && (
                <span
                  className={clsx({
                    [styles.subscriptionStatus]: true,
                    [styles[subscriptionStatus.className]]: true,
                  })}
                >
                  <span>{subscriptionStatus.label}</span>
                </span>
              )}
            </>
          )}
          <div className={styles.menuListBox}>
            <MenuItem title="خانه" iconName="&#xe88a;" route="/" />
            <MenuItem title="جستجو" iconName="&#xe8b6;" route="/search" />
            <MenuItem route="/c/movie" title="فیلم" iconName="&#xe04b;" />
            <MenuItem route="/c/series" title="سریال" iconName="&#xe8da;" />
            <MenuItem route="/lc/kids" title="پخش زنده کودک" withBulletTrigger />
            {!!auth.user && <MenuItem route="/watch-list" title="لیست تماشا" iconName="&#xe866;" />}
            {/* {!!auth.user && (
              <MenuItem route="/profile" title="تغییر پروفایل" disabled={logoutContext.loading} iconName="&#xe7fd;" />
            )} */}
            <MenuItem
              title="دانلود اپلیکیشن"
              iconName="&#xf090;"
              onClick={() => window.open("https://www.sheyda.com/download")}
            />
            {!!auth.user && auth.user.ap ? (
              <MenuItem title="خروج" onClick={handleOpenLogoutDialog} disabled={logoutContext.loading}>
                {logoutContext.loading ? (
                  <div className={styles.circularProgress}>
                    <div className="spinner" />
                  </div>
                ) : (
                  <IconComponent type="outlined" class="icon" name="&#xe9ba;" />
                )}
              </MenuItem>
            ) : (
              <MenuItem route="/login" title="ورود" iconName="&#xe7fd;" />
            )}
          </div>
        </nav>
        <div className={styles.appVersion}>
          <FocusableButton
            fullWidth
            isCentered
            focusKey="app-version"
            label={`نسخه${appVersion}`}
            startIcon="&#xe88e;"
            onClick={() => history.push("/dev")}
          />
        </div>
      </div>

      {showLogoutDialog && (
        <ConfirmationDialog
          open={showLogoutDialog}
          onClose={() => setShowLogoutDialog(false)}
          title="از حساب کاربری خود خارج میشوید؟"
          onConfirm={handleLogout}
          confirmTitle="خروج"
        />
      )}
    </FocusContext.Provider>
  );
};

export default Sidebar;
